import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Calendar, Mail, Phone, Building2, DollarSign, Trash2, User, MessageSquare, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TaskForm } from "../tasks/TaskForm";
import { TaskList } from "../tasks/TaskList";
import { useState } from "react";
import { RefetchOptions } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { LeadDetailsDialog } from "./LeadDetailsDialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

interface LeadCardProps {
  lead: any;
  onLeadClick?: (lead: any) => void;
  refetch?: (options?: RefetchOptions | undefined) => Promise<any>;
}

export const LeadCard = ({ lead, onLeadClick, refetch }: LeadCardProps) => {
  const [showTasks, setShowTasks] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: assignedUser } = useQuery({
    queryKey: ['user', lead.assigned_to],
    queryFn: async () => {
      if (!lead.assigned_to) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', lead.assigned_to)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!lead.assigned_to
  });

  const handleDelete = async () => {
    if (isDeleting) return;

    try {
      setIsDeleting(true);
      
      // Use the edge function to delete the lead
      const { error } = await supabase.functions.invoke("lead-operations", {
        body: { 
          action: 'delete',
          leadId: lead.id 
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Lead deleted successfully",
      });

      refetch?.();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete lead",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };

  const confirmDeleteHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDelete(true);
  };

  // Get last 2 events for preview
  const recentEvents = lead.events && lead.events.length > 0 
    ? [...lead.events]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 2) 
    : [];

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case "call":
        return <Phone className="w-3 h-3 text-blue-500" />;
      case "email":
        return <Mail className="w-3 h-3 text-green-500" />;
      case "meeting":
        return <Calendar className="w-3 h-3 text-purple-500" />;
      default:
        return <MessageSquare className="w-3 h-3 text-gray-500" />;
    }
  };

  return (
    <>
      <div
        className="bg-card rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setShowDetails(true)}
      >
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-semibold">{lead.name || 'Unnamed Lead'}</h3>
            {lead.contacts?.name && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                <Building2 className="w-4 h-4" />
                <span>{lead.contacts.name}</span>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={confirmDeleteHandler}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-4 space-y-2">
          {lead.contacts?.email && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="w-4 h-4" />
              <span>{lead.contacts.email}</span>
            </div>
          )}
          {lead.contacts?.phone && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="w-4 h-4" />
              <span>{lead.contacts.phone}</span>
            </div>
          )}
          {lead.created_at && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>Created {format(new Date(lead.created_at), 'MMM d, yyyy')}</span>
            </div>
          )}
          {lead.price > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <DollarSign className="w-4 h-4" />
              <span>
                ${lead.price} 
                {lead.pricing_type === 'recurring' ? ' /month' : ' one-time'}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="w-4 h-4" />
            <span>
              {assignedUser ? assignedUser.full_name || assignedUser.email : 'Unassigned'}
            </span>
          </div>
          
          {recentEvents.length > 0 && (
            <div className="mt-2 pt-2 border-t">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Recent Activity
              </div>
              {recentEvents.map((event: any) => (
                <div key={event.id} className="flex items-start gap-2 text-xs text-muted-foreground mt-1">
                  <div className="mt-0.5">
                    {getEventIcon(event.event_type)}
                  </div>
                  <div className="flex-1 truncate">
                    <span className="font-medium capitalize">
                      {event.event_type}:
                    </span>{" "}
                    {event.description.length > 30 
                      ? `${event.description.substring(0, 30)}...` 
                      : event.description}
                  </div>
                  <div className="flex items-center whitespace-nowrap">
                    <Clock className="w-3 h-3 mr-1" />
                    {format(new Date(event.created_at), "MMM d")}
                  </div>
                </div>
              ))}
              {lead.events && lead.events.length > 2 && (
                <div className="text-xs text-muted-foreground mt-1 text-right">
                  +{lead.events.length - 2} more
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">Add Task</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px] h-[80vh]">
                <DialogHeader>
                  <DialogTitle>Create Task</DialogTitle>
                </DialogHeader>
                <ScrollArea className="h-[calc(80vh-8rem)] px-4">
                  <TaskForm 
                    leadId={lead.id} 
                    onSuccess={() => {
                      toast({
                        title: "Success",
                        description: "Task created successfully"
                      });
                    }}
                  />
                </ScrollArea>
              </DialogContent>
            </Dialog>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowTasks(!showTasks)}
            >
              {showTasks ? "Hide Tasks" : "Show Tasks"}
            </Button>
          </div>

          {showTasks && (
            <div className="mt-4">
              <TaskList leadId={lead.id} />
            </div>
          )}
        </div>
      </div>

      <LeadDetailsDialog
        lead={lead}
        open={showDetails}
        onOpenChange={setShowDetails}
        onUpdate={refetch}
      />
      
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        onConfirm={handleDelete}
        title="Delete Lead"
        description="Are you sure you want to delete this lead? This action cannot be undone."
      />
    </>
  );
};
